import { useRef } from "react";
import { type Key, type ValidationResult } from "react-aria-components";

import { Autocomplete } from "../Autocomplete/Autocomplete";
import { Icon } from "../Icon/Icon";

export interface Place {
  id: string;
  place_name: string;
  place_type: string[];
  center: [number, number];
  context?: Array<{
    id: string;
    text: string;
    short_code?: string;
  }>;
  properties?: {
    address?: string;
  };
  address?: string;
  text?: string;
}

interface MapboxResponse {
  features: Place[];
  type: string;
}

// Type guard to validate MapboxResponse shape
function isMapboxResponse(data: unknown): data is MapboxResponse {
  return (
    typeof data === "object" &&
    data !== null &&
    "features" in data &&
    Array.isArray(data.features) &&
    "type" in data &&
    typeof data.type === "string"
  );
}

type SearchType = "country" | "region" | "district" | "place" | "locality" | "neighborhood" | "address" | "street";

/**
 * PlaceSearch
 *
 * Location search component with autocomplete; emits a `Place` value on
 * selection.
 */
export interface PlaceSearchProps {
  label?: string;
  selectedKey?: Key | null;
  defaultSelectedKey?: Key | null;
  onSelectionChange?: (key: Key | null) => void;
  placeholder?: string;
  errorMessage?: string | ((validation: ValidationResult) => string);
  description?: string;
  size?: "sm" | "md" | "lg" | "xl";
  tooltip?: string;
  isRequired?: boolean;
  isDisabled?: boolean;
  showErrors?: boolean;
  autoFocus?: boolean;
  onSelect: (place: Place) => void;
  searchTypes?: SearchType[];
  defaultFilter?: (textValue: string, inputValue: string) => boolean;
  validationResult?: ValidationResult;
  countryRestrictions?: string[]; // Array of ISO 3166-1 alpha-2 country codes
  proximity?: "ip" | [number, number]; // Either "ip" for IP-based location or [longitude, latitude] coordinates
  hideCountry?: boolean; // Hide country from results (useful when using countryRestrictions)
  showIcon?: boolean; // Show location type icons in results (default: true)
  useMobileTray?: boolean; // Whether to use a Tray (bottom sheet) on mobile devices instead of a Popover (default: true)
  className?: string;
}

const MAPBOX_TOKEN = process.env.NEXT_PUBLIC_MAPBOX_ACCESS_TOKEN || "";

export function PlaceSearch({
  label,
  selectedKey,
  defaultSelectedKey,
  onSelectionChange,
  placeholder = "Enter a location...",
  errorMessage,
  description,
  size = "md",
  tooltip,
  isRequired,
  isDisabled,
  showErrors = true,
  autoFocus = false,
  onSelect,
  searchTypes = ["place", "address"],
  defaultFilter,
  validationResult,
  countryRestrictions,
  proximity = "ip", // Set IP-based location as default
  hideCountry = false,
  showIcon = true,
  useMobileTray = true,
  className,
}: PlaceSearchProps) {
  // Use a WeakMap to store additional data for each item
  const itemDataRef = useRef(
    new WeakMap<
      { id: string; name: string },
      {
        type: string;
        center: [number, number];
        context?: Array<{ id: string; text: string; short_code?: string }>;
        address?: string;
        text?: string;
        properties?: { address?: string };
      }
    >()
  );

  // Store the last loaded items for use in selection
  const loadedItemsRef = useRef<{ id: string; name: string }[]>([]);

  const requestConfig = {
    requestType: "REST" as const,
    method: "GET" as const,
    url: "https://api.mapbox.com/geocoding/v5/mapbox.places/{q}.json",
    extraParams: {
      access_token: MAPBOX_TOKEN,
      types: searchTypes.join(","),
      limit: "5",
      ...(countryRestrictions?.length ? { country: countryRestrictions.join(",").toLowerCase() } : {}),
      ...(proximity
        ? {
            proximity: proximity === "ip" ? "ip" : proximity.join(","),
          }
        : {}),
    },
    shouldLoad: (filterText: string) => {
      return filterText.length > 0;
    },
    transformResponse: (data: unknown) => {
      if (!isMapboxResponse(data)) return [];

      const items = data.features.map((feature) => {
        const item = {
          id: feature.id,
          name: feature.place_name,
        };
        // Store additional data in the WeakMap
        itemDataRef.current.set(item, {
          type: feature.place_type[0],
          center: feature.center,
          context: feature.context,
          address: feature.address,
          text: feature.text,
          properties: feature.properties,
        });
        return item;
      });

      // Store the items for later use
      if (items.length > 0) {
        loadedItemsRef.current = items;
      }

      return items;
    },
  };

  const renderItem = (item: { id: string; name: string }) => {
    const itemData = itemDataRef.current.get(item);

    // Split the place name into parts by commas for better formatting
    const nameParts = item.name.split(", ");
    const primaryText = nameParts[0];

    // If hideCountry is true, remove the last part (country) from secondary text
    let secondaryParts = nameParts.slice(1);
    if (hideCountry && secondaryParts.length > 0) {
      // Remove the last element (typically the country)
      secondaryParts = secondaryParts.slice(0, -1);
    }
    const secondaryText = secondaryParts.join(", ");

    return (
      <div className="flex items-center gap-3 py-0.5">
        {showIcon && (
          <Icon
            name={
              itemData?.type === "address"
                ? "MapPin"
                : itemData?.type === "place"
                  ? "Buildings"
                  : itemData?.type === "region"
                    ? "MapPinArea"
                    : itemData?.type === "country"
                      ? "Flag"
                      : "Circle"
            }
            className="h-5 w-5 flex-shrink-0 text-text-muted"
          />
        )}
        <div className="flex min-w-0 flex-col">
          <span className="truncate font-medium text-text-body">{primaryText}</span>
          {secondaryText && <span className="truncate text-sm text-text-muted">{secondaryText}</span>}
        </div>
      </div>
    );
  };

  const renderLeftIcon = (isLoading: boolean) => {
    if (isLoading) {
      return <Icon name="CircleNotch" className="h-4 w-4 animate-spin text-text-muted" />;
    }
    return <Icon name="MagnifyingGlass" className="h-4 w-4 text-text-muted" />;
  };

  return (
    <Autocomplete
      data-testid="autocomplete"
      data-config={JSON.stringify({
        types: searchTypes.join(","),
        country: countryRestrictions?.join(",")?.toLowerCase(),
        proximity: proximity === "ip" ? "ip" : proximity?.join(","),
      })}
      label={label || undefined}
      selectedKey={selectedKey}
      defaultSelectedKey={defaultSelectedKey}
      onSelectionChange={(key) => {
        onSelectionChange?.(key);
        if (key) {
          // Use the stored items instead of creating an empty array
          const items = loadedItemsRef.current;
          const selected = items.find((item) => item.id === key);

          if (selected) {
            const itemData = itemDataRef.current.get(selected);

            if (itemData) {
              onSelect({
                id: selected.id,
                place_name: selected.name,
                place_type: [itemData.type],
                center: itemData.center,
                context: itemData.context,
                address: itemData.address,
                text: itemData.text,
                properties: itemData.properties,
              });
            }
          }
        }
      }}
      requestConfig={requestConfig}
      placeholder={placeholder}
      errorMessage={errorMessage}
      description={description}
      size={size}
      tooltip={tooltip}
      isRequired={isRequired}
      isDisabled={isDisabled}
      showErrors={showErrors}
      autoFocus={autoFocus}
      renderItem={renderItem}
      renderLeftIcon={renderLeftIcon}
      defaultFilter={defaultFilter}
      validationResult={validationResult}
      hideChevron
      useMobileTray={useMobileTray}
      className={className}
    />
  );
}
